export type AutomationStatus = 'sent' | 'drafted' | 'skipped' | 'failed';
export type AutomationChannel = 'email' | 'sms' | 'portal' | 'graph_draft';
export type AutomationDepartment = 'intake' | 'calendar' | 'closing' | 'general';
export type AutomationTrigger =
  | 'cron'
  | 'inbound_email'
  | 'staff_action'
  | 'system_event';

export interface AutomationActivityItemDto {
  id: string;
  guid: string;
  sourceTable:
    | 'automation_activity'
    | 'user_note'
    | 'message'
    | 'offer'
    | 'court_event'
    | 'ticket_stamp';
  createdAt: string;
  department: AutomationDepartment;
  type: string;
  typeLabel: string;
  status: AutomationStatus;
  channel: AutomationChannel;
  trigger: AutomationTrigger;
  recipient: string | null;
  recipientName: string | null;
  subject: string | null;
  content: string | null;
  statusReason: string | null;
  targetKind: 'case' | 'lead';
  ticketId: number | null;
  ticketGuid: string | null;
  caseNumber: string | null;
  userId: number | null;
  userGuid: string | null;
  clientName: string | null;
  courtId: number | null;
  courtName: string | null;
  intakeSessionId: number | null;
  leadRef: string | null;
}

export interface AutomationActivityResponseDto {
  items: AutomationActivityItemDto[];
  total: number;
  limit: number;
  offset: number;
  statusCounts: Record<AutomationStatus, number>;
  availableTypes: Array<{
    type: string;
    label: string;
    department: AutomationDepartment;
    count: number;
  }>;
  isCapped?: boolean;
}

export interface FetchAutomationActivityOptions {
  search?: string;
  type?: string;
  status?: AutomationStatus | 'all';
  department?: AutomationDepartment | 'all';
  dateFrom?: string;
  dateTo?: string;
  ticketId?: number;
  limit?: number;
  offset?: number;
}

export async function fetchAutomationActivity(
  options: FetchAutomationActivityOptions = {},
): Promise<AutomationActivityResponseDto> {
  const params = new URLSearchParams();
  if (options.search?.trim()) params.set('search', options.search.trim());
  if (options.type && options.type !== 'all') params.set('type', options.type);
  if (options.status && options.status !== 'all') params.set('status', options.status);
  if (options.department && options.department !== 'all') {
    params.set('department', options.department);
  }
  if (options.dateFrom) params.set('dateFrom', options.dateFrom);
  if (options.dateTo) params.set('dateTo', options.dateTo);
  if (typeof options.ticketId === 'number') {
    params.set('ticketId', String(options.ticketId));
  }
  params.set('limit', String(options.limit ?? 100));
  params.set('offset', String(options.offset ?? 0));

  const response = await fetch(
    `${process.env.API_HOST || ''}/api/v1/automation-activity?${params.toString()}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch automation activity: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
}
